import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchStreamData } from '../services/radioService';

const THROTTLE_INTERVAL = 10000; // 10 seconds in milliseconds

export function useRadioStream(streamUrl, apiUrl, isFirstMount = true) {
    const [streamState, setStreamState] = useState({ isPlaying: false, volume: 1 });
    const [metadata, setMetadata] = useState(null);
    const [elapsed, setElapsed] = useState(0);
    const [error, setError] = useState(null);

    // Refs for tracking API calls and updates
    const lastFetchTime = useRef(0);
    const fetchInProgress = useRef(false);
    const audioRef = useRef(null);
    const elapsedIntervalRef = useRef(null);

    // Initialize audio element
    useEffect(() => {
        if (!audioRef.current) {
            audioRef.current = new Audio();
            audioRef.current.preload = 'none';
            audioRef.current.volume = streamState.volume;
        }

        if (streamUrl) {
            audioRef.current.src = streamUrl;
        }

        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
            if (elapsedIntervalRef.current) {
                clearInterval(elapsedIntervalRef.current);
            }
        };
    }, [streamUrl]);

    // Continuous time tracking
    useEffect(() => {
        // Start time tracking interval
        if (elapsedIntervalRef.current) {
            clearInterval(elapsedIntervalRef.current);
        }

        elapsedIntervalRef.current = setInterval(() => {
            setElapsed(prev => {
                if (!metadata?.now_playing?.duration) return prev + 1;
                const duration = metadata.now_playing.duration;
                const next = prev + 1;
                return next >= duration ? 0 : next;
            });
        }, 1000);

        return () => {
            if (elapsedIntervalRef.current) {
                clearInterval(elapsedIntervalRef.current);
            }
        };
    }, [metadata?.now_playing?.duration]);

    // Throttled metadata fetch
    const fetchMetadata = useCallback(async (force = false) => {
        if (!apiUrl) {
            setError('No API URL provided');
            return;
        }

        const now = Date.now();
        const timeSinceLastFetch = now - lastFetchTime.current;

        // Skip if fetch is in progress or throttle interval hasn't passed
        if (fetchInProgress.current || (!force && timeSinceLastFetch < THROTTLE_INTERVAL)) {
            return;
        }

        fetchInProgress.current = true;

        try {
            const data = await fetchStreamData(apiUrl);
            const previousTrack = metadata?.now_playing?.song?.id;
            const newTrack = data?.now_playing?.song?.id;

            setMetadata(data);
            lastFetchTime.current = now;

            // Reset elapsed time only if the track has changed
            if (data?.now_playing && previousTrack !== newTrack) {
                setElapsed(data.now_playing.elapsed || 0);
            }
        } catch (err) {
            console.error('Failed to fetch metadata:', err);
            setError('Failed to fetch metadata');
        } finally {
            fetchInProgress.current = false;
        }
    }, [apiUrl, metadata?.now_playing?.song?.id]);

    // Continuous metadata fetch regardless of play state
    useEffect(() => {
        if (apiUrl) {
            fetchMetadata(true);
            const metadataInterval = setInterval(() => fetchMetadata(false), THROTTLE_INTERVAL);

            return () => {
                clearInterval(metadataInterval);
                fetchInProgress.current = false;
            };
        }
    }, [fetchMetadata, apiUrl]);

    const controls = {
        togglePlay: async () => {
            try {
                if (streamState.isPlaying) {
                    audioRef.current.pause();
                    setStreamState(prev => ({ ...prev, isPlaying: false }));
                } else {
                    await audioRef.current.play();
                    setStreamState(prev => ({ ...prev, isPlaying: true }));
                }
            } catch (err) {
                console.error('Failed to toggle playback:', err);
                setError('Failed to play stream');
            }
        },
        setVolume: (value) => {
            audioRef.current.volume = value;
            setStreamState(prev => ({ ...prev, volume: value }));
        }
    };

    return {
        isPlaying: streamState.isPlaying,
        volume: streamState.volume,
        metadata,
        elapsed,
        error,
        controls
    };
} 